import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, onSnapshot, addDoc, increment, updateDoc, getDocs, getDoc, doc, runTransaction, Timestamp } from 'firebase/firestore';
import VehicleForm from './ManageVehicleImports/VehicleForm';
import VehicleTable from './ManageVehicleImports/VehicleTable';
import { generateVehicleGIN } from '../helpers/GIN';
import ScheduleAppointment from '../Tasks/Modals/ScheduleAppointment';
import EditVehicleModal from './ManageVehicleImports/EditVehicleModal';
import VehiclePhotosModal from './ManageVehicleImports/VehiclePhotosModal';
import BreadCrumb from '../BreadCrumb';
import logger from '../../utils/logger';



const pages = [
  { name: 'Service Advisor', to: '/vehicles', component: Link, current: false },
  { name: 'Manage Vehicles', to: '/vehicles/manage', component: Link, current: true },
];

const ManageVehicle = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [selectedVehicleForAppointment, setSelectedVehicleForAppointment] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formState, setFormState] = useState({
    vehicleMake: '',
    vehicleModel: '',
    year: '',
    VIN: '',
    registrationNumber: '',
    engineType: '',
  });

  useEffect(() => {
    const unsubscribeFromVehicles = onSnapshot(collection(db, 'vehicles'), (snapshot) => {
      const vehicleList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setVehicles(vehicleList);
    });

    const unsubscribeFromCustomers = onSnapshot(collection(db, 'customers'), (snapshot) => {
      const customerList = snapshot.docs.map((doc) => ({
        value: doc.id,
        label: `${doc.data().firstName} ${doc.data().lastName}`,
      }));
      setCustomers(customerList);
    });

    return () => {
      unsubscribeFromVehicles();
      unsubscribeFromCustomers();
    };
  }, []);



  const handleInputChange = (e) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const addVehicle = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      alert('Please select a customer first.');
      return;
    }

    const customerRef = doc(db, 'customers', selectedCustomer.value);
    const customerDoc = await getDoc(customerRef);

    if (!customerDoc.exists()) {
      alert('Customer not found.');
      return;
    }

    if (!formState.vehicleMake || !formState.vehicleModel || !formState.year || !formState.VIN || !formState.registrationNumber) {
      alert('Please fill all the fields.');
      return;
    }

     // Fetch existing vehicles for this customer
     const vehiclesSnapshot = await getDocs(collection(db, 'vehicles'));
     const customerVehicles = vehiclesSnapshot.docs.filter(vehicleDoc => vehicleDoc.data().customerId === selectedCustomer.value);
   
      // Generate the vehicle-specific GIN
    const newVehicleGIN = generateVehicleGIN(selectedCustomer.value, customerVehicles.length + 1);

    const vehicleData = {
      ...formState,
      customerId: selectedCustomer.value,
      GIN: newVehicleGIN,
      registeredAt: Timestamp.fromDate(new Date()),
      serviceHistory: [],
      ...(customerDoc.data().agentId && { agentId: customerDoc.data().agentId }),
    };

    try {
      await addDoc(collection(db, 'vehicles'), vehicleData);

      await updateDoc(customerRef, {
        vehicleCount: increment(1),
      });

      setFormState({
        vehicleMake: '',
        vehicleModel: '',
        year: '',
        VIN: '',
        registrationNumber: '',
        engineType: '',
      });
      setSelectedCustomer(null);
    } catch (error) {
      logger.error('Error adding vehicle:', error);
    }
  };

  const deleteVehicle = async (vehicleId, customerId) => {
    const vehicleRef = doc(db, 'vehicles', vehicleId);
    const customerRef = doc(db, 'customers', customerId);

    await runTransaction(db, async (transaction) => {
      transaction.delete(vehicleRef);

      transaction.update(customerRef, {
        vehicleCount: increment(-1),
      });
    });
  };

  const viewInspectionReport = async (vehicle) => {
    try {
      const inspectionDocRef = doc(db, 'inspections', vehicle.registrationNumber);
      const inspectionDocSnap = await getDoc(inspectionDocRef);

      let inspectionData = {};
      if (inspectionDocSnap.exists()) {
        inspectionData = inspectionDocSnap.data();
      } else {
        logger.info('No inspection data found for this vehicle.');
      }

      navigate('/vehicles/inspectionReport', { state: { vehicle, formData: inspectionData } });
    } catch (error) {
      logger.error('Error fetching inspection data:', error);
    }
  };

  const handleYearSelection = (event) => {
    setFormState({
      ...formState,
      year: event.target.value,
    });
  };

  const handleScheduleAppointment = (vehicle) => {
    setSelectedVehicleForAppointment(vehicle);
    setIsAppointmentModalOpen(true);
  };

  // Edit Vehicle Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [vehicleToEdit, setVehicleToEdit] = useState(null);

  const openEditModal = (vehicle) => {
    setVehicleToEdit(vehicle);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setVehicleToEdit(null);
  };

  // Vehicle Photos Modal State
  const [isPhotosModalOpen, setIsPhotosModalOpen] = useState(false);
  const [vehicleForPhotos, setVehicleForPhotos] = useState(null);

  const openPhotosModal = (vehicle) => {
    setVehicleForPhotos(vehicle);
    setIsPhotosModalOpen(true);
  };

  const closePhotosModal = () => {
    setIsPhotosModalOpen(false);
    setVehicleForPhotos(null);
  };

  const saveVehicleEdits = async (updated) => {
    try {
      const ref = doc(db, 'vehicles', updated.id);
      await updateDoc(ref, {
        vehicleMake: updated.vehicleMake || '',
        vehicleModel: updated.vehicleModel || '',
        year: updated.year || '',
        VIN: updated.VIN || '',
        registrationNumber: updated.registrationNumber || '',
        engineType: updated.engineType || '',
      });
      closeEditModal();
    } catch (e) {
      logger.error('Failed to update vehicle', e);
    }
  };


 

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-gray-900 mb-2">
            Vehicle Management
          </h1>
          <p className="text-gray-600">
            Register customers, add vehicles, and manage vehicle information
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 overflow-visible">
          {/* Form Section - Takes 5 columns on large screens */}
          <div className="lg:col-span-5">
            <VehicleForm
              customers={customers}
              selectedCustomer={selectedCustomer}
              setSelectedCustomer={setSelectedCustomer}
              formState={formState}
              handleInputChange={handleInputChange}
              handleYearSelection={handleYearSelection}
              addVehicle={addVehicle}
            />
          </div>

          {/* Table Section - Takes 7 columns on large screens */}
          <div className="lg:col-span-7">
            <VehicleTable
              vehicles={vehicles}
              selectedCustomer={selectedCustomer}
              navigate={navigate}
              handleScheduleAppointment={handleScheduleAppointment}
              viewInspectionReport={viewInspectionReport}
              deleteVehicle={deleteVehicle}
              openEditModal={openEditModal}
              openPhotosModal={openPhotosModal}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      {isAppointmentModalOpen && (
        <ScheduleAppointment
          vehicle={selectedVehicleForAppointment}
          customer={customers.find(c => c.value === selectedVehicleForAppointment.customerId)}
          onClose={() => setIsAppointmentModalOpen(false)}
        />
      )}

      {isEditModalOpen && vehicleToEdit && (
        <EditVehicleModal
          vehicle={vehicleToEdit}
          onClose={closeEditModal}
          onSave={saveVehicleEdits}
        />
      )}

      {isPhotosModalOpen && vehicleForPhotos && (
        <VehiclePhotosModal
          vehicle={vehicleForPhotos}
          onClose={closePhotosModal}
        />
      )}
    </div>
  );
};

export default ManageVehicle;
